import { defineComponent, ref, reactive, computed } from '@vue/composition-api';
import { trackEvent } from './../../../utils/index';
import { useAuthorizeTTVideoApi, useTTVideoInfoApi, useDoubleTTAuthorizeApi } from '@tt4b/ttam-hooks';
import { i18n } from './../../../i18n';

export function useAuthorizePost({
  teaParams
}: { teaParams: Record<string, string>} = { teaParams: {}}) {
  const STATES = {
    INIT: 1, // 初始状态
    FETCHED: 2, // 拉取到链接对应信息
    SUCCESS: 3, // 成功提交授权
  };
  const authorizationFormRef = ref();
  const authorizationForm = reactive({
    videoCode: '',
    fromVideoCode: '',
  });
  const searchLoading = ref(false);
  const state = ref(STATES.INIT);
  const nativeVideo = ref<any>({});
  const videoCodeError = ref();
  const fromVideoCodeError = ref();
  const hasOriginalPost = ref(false); // 是否是跟拍&合拍视频
  const identityInfo = ref({}); // 授权码的视频

  const isStateInit = computed(() => state.value === STATES.INIT);
  const isStateFetched = computed(() => state.value === STATES.FETCHED);
  const hasNativeVideo = computed(() => Boolean(Object.keys(nativeVideo.value).length));
  const title = computed(() =>
    hasOriginalPost.value
      ? i18n.t('module_library_native_stitch_post_title')
      : i18n.t('module_affiliate_auth_code_desc'),
  );
  const videoCode = computed({
    get() {
      return authorizationForm?.videoCode || '';
    },
    set(val) {
      authorizationForm.videoCode = String(val);
    },
  });

  const fromVideoCode = computed({
    get() {
      return authorizationForm?.fromVideoCode || '';
    },
    set(val) {
      authorizationForm.fromVideoCode = String(val);
    },
  });

  // 切换视频
  function onClearCode() {
    videoCode.value = '';
    fromVideoCode.value = '';
    videoCodeError.value = '';
    fromVideoCodeError.value = '';
    nativeVideo.value = {};
    hasOriginalPost.value = false;
    searchLoading.value = false;
    state.value = STATES.INIT;
  }
  function clearStatus(status = STATES.INIT) {
    searchLoading.value = true;
    state.value = status;
    videoCodeError.value = '';
    fromVideoCodeError.value = '';
  }
  function createSingleAuthorization({ onSuccess }: { onSuccess?: () => void } = {}) {
    if (!hasNativeVideo.value || !authorizationFormRef.value) {
      return;
    }

    authorizationFormRef.value?.validateField('videoCode', error => {
      if (!error) {
        trackEvent('confirm_single_authorization', {
          ...teaParams
        });
        searchLoading.value = true;
        videoCodeError.value = '';

        useAuthorizeTTVideoApi({ videoCode: videoCode.value as string })
          .then(() => {
            searchLoading.value = false;
            state.value = STATES.SUCCESS;
            onSuccess?.();
          })
          .catch(err => {
            searchLoading.value = false;
            if (err?.msg) {
              videoCodeError.value = err.msg;
              trackEvent('confirm_single_authorization_fail', {
                message: err.msg || '',
                ...teaParams
              });
            }
          });
      }
    });
  }
  /** 双授权 */
  function createDoubleAuthorization({ onSuccess }: { onSuccess?: () => void } = {}) {
    if (!authorizationFormRef.value) {
      return;
    }

    authorizationFormRef.value?.validate(isValidate => {
      if (isValidate) {
        trackEvent('confirm_double_authorization', {
          ...teaParams
        });
        searchLoading.value = true;
        fromVideoCodeError.value = '';

        useDoubleTTAuthorizeApi({
          videoCode: videoCode.value as string,
          fromVideoCode: fromVideoCode.value as string,
          onlyValidate: false,
        })
          .then(resp => {
            searchLoading.value = false;
            state.value = STATES.SUCCESS;
            onSuccess?.();
          })
          .catch(err => {
            searchLoading.value = false;
            if (err?.msg) {
              fromVideoCodeError.value = err.msg;
              trackEvent('confirm_double_authorization_fail', {
                message: err.msg || '',
                ...teaParams
              });
            }
          });
      }
    });
  }
  /** 创建授权 */
  function handleCreateAuthorization({ onSuccess }: { onSuccess?: () => void } = {}) {
    if (hasOriginalPost.value) {
      createDoubleAuthorization({ onSuccess });
    } else {
      createSingleAuthorization({ onSuccess });
    }
  }
  function handleTtVideo(ttVideo) {
    // 非双授权白名单 直接展示搜索结果
    if (ttVideo?.isMultiDuetStitch) {
      videoCodeError.value = i18n.t('module_library_native_video_link_is_multi_duet_stitch');
      trackEvent('video_code_validate_fail', {
        message: videoCodeError.value || '',
        ...teaParams
      });

      return;
    }

    nativeVideo.value = ttVideo;
    identityInfo.value = {
      id: ttVideo.coreUserId,
    };
    if (state.value === STATES.INIT) {
      state.value = STATES.FETCHED;
    }

    // 属于双授权的视频
    if (ttVideo?.fromItemId) {
      hasOriginalPost.value = true;
    }

    trackEvent('video_code_validate_success', {
      post_type: hasOriginalPost.value ? 'double_authorization' : 'single_authorization',
      ...teaParams
    });
  }
  /** 校验first视频授权码 */
  function getNativeVideoInfo() {
    if (!authorizationFormRef.value) {
      return;
    }

    authorizationFormRef.value?.validateField('videoCode', error => {
      if (!error) {
        clearStatus();
        nativeVideo.value = {};

        useTTVideoInfoApi({ videoCode: videoCode.value as string })
          .then(resp => {
            searchLoading.value = false;

            const { ttVideo = {} } = resp;
            handleTtVideo(ttVideo);
          })
          .catch(err => {
            searchLoading.value = false;
            if (err?.msg) {
              videoCodeError.value = err.msg;
              trackEvent('video_code_validate_fail', {
                message: err.msg || '',
                ...teaParams
              });
            }
          });
      }
    });
  }

  function getFromNativeVideoInfo({ onSuccess }: { onSuccess?: () => void } = {}) {
    if (!authorizationFormRef.value) {
      return;
    }

    authorizationFormRef.value?.validate(isValidate => {
      if (isValidate) {
        clearStatus(STATES.FETCHED);

        useDoubleTTAuthorizeApi({
          videoCode: videoCode.value as string,
          fromVideoCode: fromVideoCode.value as string,
          onlyValidate: true,
        })
          .then(resp => {
            trackEvent('double_authorization_validate_success', {
              ...teaParams
            });

            // 若校验成功，则立刻调用授权操作
            handleCreateAuthorization({
              onSuccess,
            });
            searchLoading.value = false;
          })
          .catch(err => {
            searchLoading.value = false;
            if (err?.msg) {
              fromVideoCodeError.value = err.msg;
              trackEvent('double_authorization_validate_fail', {
                message: err.msg || '',
                ...teaParams
              });
            }
          });
      }
    });
  }
  return {
    authorizationFormRef,
    authorizationForm,
    searchLoading,
    state,
    nativeVideo,
    videoCodeError,
    fromVideoCodeError,
    hasOriginalPost,
    isStateInit,
    isStateFetched,
    hasNativeVideo,
    title,
    videoCode,
    fromVideoCode,
    identityInfo,

    onClearCode,
    clearStatus,
    createSingleAuthorization,
    createDoubleAuthorization,
    handleCreateAuthorization,
    handleTtVideo,
    getNativeVideoInfo,
    getFromNativeVideoInfo,
  };
}
